import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import { toast } from "sonner";
import { 
  MessageSquare, Mail, Send, AlertCircle, CheckCircle, Clock, 
  Filter, RefreshCw, Settings, ArrowLeft, FileText, Activity,
  Smartphone, Server, Search, Eye
} from "lucide-react";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";

// Stat Card
function StatCard({ title, value, icon: Icon, color = "primary", subtext }) {
  const colors = {
    primary: "bg-primary/10 text-primary",
    green: "bg-green-100 text-green-600",
    red: "bg-red-100 text-red-600",
    amber: "bg-amber-100 text-amber-600",
    blue: "bg-blue-100 text-blue-600",
  };
  
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="text-2xl font-bold text-slate-800">{value}</p>
          {subtext && <p className="text-xs text-slate-400">{subtext}</p>}
        </div>
      </div>
    </div>
  );
}

// Status Badge
function StatusBadge({ status }) {
  const styles = {
    sent: "bg-green-100 text-green-700",
    delivered: "bg-blue-100 text-blue-700",
    pending: "bg-amber-100 text-amber-700",
    failed: "bg-red-100 text-red-700",
    queued: "bg-slate-100 text-slate-700",
  };
  
  const labels = {
    sent: "Envoyé",
    delivered: "Délivré",
    pending: "En attente",
    failed: "Échoué",
    queued: "En file",
  };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
      {labels[status] || status}
    </span>
  );
}

// Channel Badge
function ChannelBadge({ channel }) {
  if (channel === "whatsapp") {
    return (
      <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <Smartphone className="w-3 h-3" />
        WhatsApp
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
      <Mail className="w-3 h-3" />
      Email
    </span>
  );
}

export default function CommunicationCenterPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("logs");
  const [loading, setLoading] = useState(true);
  
  // Data
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [providerStatus, setProviderStatus] = useState(null);
  
  // Filters
  const [channelFilter, setChannelFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, logsRes, templatesRes, providerRes] = await Promise.all([
        api.get("/communication/stats?days=30").catch(() => ({ data: { stats: {} } })),
        api.get("/communication/logs?limit=100").catch(() => ({ data: { logs: [] } })),
        api.get("/communication/templates").catch(() => ({ data: { templates: [] } })),
        api.get("/communication/providers/status").catch(() => ({ data: {} })),
      ]);
      
      setStats(statsRes.data.stats);
      setLogs(logsRes.data.logs);
      setTemplates(templatesRes.data.templates);
      setProviderStatus(providerRes.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des données");
      // error handled by toast
    } finally {
      setLoading(false);
    }
  };
  
  const filteredLogs = logs.filter(log => {
    if (channelFilter && log.channel !== channelFilter) return false;
    if (statusFilter && log.status !== statusFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        log.recipientContact?.toLowerCase().includes(query) ||
        log.templateSlug?.toLowerCase().includes(query) ||
        log.recipientRole?.toLowerCase().includes(query)
      );
    }
    return true;
  });
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };
  
  const renderStats = () => {
    if (!stats) return null;
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard 
          title="Total envoyés" 
          value={stats.total?.sent || 0}
          icon={Send}
          color="green"
          subtext="30 derniers jours"
        />
        <StatCard 
          title="WhatsApp" 
          value={stats.whatsapp?.sent || 0}
          icon={Smartphone}
          color="primary"
          subtext={`${stats.whatsapp?.failed || 0} échecs`}
        />
        <StatCard 
          title="Email" 
          value={stats.email?.sent || 0}
          icon={Mail}
          color="blue"
          subtext={`${stats.email?.failed || 0} échecs`}
        />
        <StatCard 
          title="Échecs" 
          value={stats.total?.failed || 0}
          icon={AlertCircle}
          color="red"
          subtext="À investiguer"
        />
      </div>
    );
  };
  
  const renderProviderStatus = () => {
    if (!providerStatus) return null;

    const providers = [
      {
        key: "twilio_sms",
        name: "Twilio (SMS + OTP)",
        subtitle: "SMS patients + OTP Verify",
        color: "green",
        fields: [
          ["Account SID", providerStatus.twilio_sms?.account_sid_set],
          ["Auth Token", providerStatus.twilio_sms?.auth_token_set],
          ["Phone Number", providerStatus.twilio_sms?.phone_number_set],
          ["Verify Service", providerStatus.twilio_sms?.verify_service_set],
        ],
        configured: providerStatus.twilio_sms?.configured,
      },
      {
        key: "twilio_whatsapp",
        name: "Twilio (WhatsApp)",
        subtitle: "Messages WhatsApp",
        color: "emerald",
        fields: [
          ["WhatsApp Number", providerStatus.twilio_whatsapp?.whatsapp_number_set],
        ],
        configured: providerStatus.twilio_whatsapp?.configured,
      },
      {
        key: "resend",
        name: "Resend (Email)",
        subtitle: "Emails professionnels (2FA, reset...)",
        color: "blue",
        fields: [
          ["API Key", providerStatus.resend?.api_key_set],
          ["Sender", providerStatus.resend?.sender_email || "-"],
        ],
        configured: providerStatus.resend?.configured,
      },
      {
        key: "infobip_fallback",
        name: "Infobip (SMS Fallback)",
        subtitle: "Fallback si Twilio indispo",
        color: "orange",
        fields: [
          ["Base URL", providerStatus.infobip_fallback?.base_url_set],
          ["API Key", providerStatus.infobip_fallback?.api_key_set],
          ["Sender", providerStatus.infobip_fallback?.sender || "-"],
        ],
        configured: providerStatus.infobip_fallback?.configured,
      },
      {
        key: "push_vapid",
        name: "Web Push (VAPID)",
        subtitle: "Notifications PWA patient",
        color: "purple",
        fields: [
          ["Cle publique", providerStatus.push_vapid?.public_key_set],
          ["Cle privee", providerStatus.push_vapid?.private_key_set],
        ],
        configured: providerStatus.push_vapid?.configured,
      },
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {providers.map((p) => (
          <div key={p.key} className="bg-white rounded-xl p-5 shadow-sm border border-slate-100" data-testid={`provider-${p.key}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg bg-${p.color}-100 flex items-center justify-center`}>
                  {p.key.startsWith("twilio") ? <Smartphone className={`w-5 h-5 text-${p.color}-600`} /> :
                   p.key === "resend" ? <Mail className={`w-5 h-5 text-${p.color}-600`} /> :
                   <Activity className={`w-5 h-5 text-${p.color}-600`} />}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">{p.name}</h3>
                  <p className="text-xs text-slate-500">{p.subtitle}</p>
                </div>
              </div>
              {p.configured ? (
                <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3" />
                  OK
                </span>
              ) : (
                <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                  <AlertCircle className="w-3 h-3" />
                  Dormant
                </span>
              )}
            </div>
            <div className="space-y-2 text-xs">
              {p.fields.map(([label, value]) => (
                <div key={label} className="flex justify-between">
                  <span className="text-slate-500">{label}</span>
                  <span className={typeof value === "boolean" ? (value ? "text-green-600" : "text-red-500") : "text-slate-700"}>
                    {typeof value === "boolean" ? (value ? "✓ Defini" : "✗ Manquant") : value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  const renderLogs = () => (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
      {/* Filters */}
      <div className="p-4 border-b border-slate-100 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
          />
        </div>
        
        <select
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
        >
          <option value="">Tous les canaux</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="email">Email</option>
        </select>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
        >
          <option value="">Tous les statuts</option>
          <option value="sent">Envoyé</option>
          <option value="failed">Échoué</option>
          <option value="pending">En attente</option>
        </select>
        
        <button
          onClick={loadData}
          className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          title="Actualiser"
        >
          <RefreshCw className={`w-4 h-4 text-slate-600 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Canal</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Destinataire</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Template</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Statut</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Événement</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredLogs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  Aucune communication trouvée
                </td>
              </tr>
            ) : (
              filteredLogs.map((log, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {formatDate(log.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <ChannelBadge channel={log.channel} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-slate-800">{log.recipientContact}</div>
                    <div className="text-xs text-slate-500">{log.recipientRole}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 font-mono text-xs">
                    {log.templateSlug}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={log.status} />
                    {log.errorMessage && (
                      <div className="text-xs text-red-500 mt-1 max-w-[200px] truncate" title={log.errorMessage}>
                        {log.errorMessage}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {log.eventType || "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
  
  const renderTemplates = () => (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
      <div className="p-4 border-b border-slate-100">
        <h3 className="font-semibold text-slate-800">Templates de messages</h3>
        <p className="text-sm text-slate-500">Gérez les templates utilisés pour les communications</p>
      </div>
      
      <div className="divide-y divide-slate-100">
        {templates.map((template, idx) => (
          <div key={idx} className="p-4 hover:bg-slate-50">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-slate-800">{template.name}</span>
                  <ChannelBadge channel={template.channel} />
                  {!template.isActive && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-500">
                      Inactif
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 font-mono mb-2">{template.slug}</p>
                <p className="text-sm text-slate-600">{template.description}</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {template.variables?.map((v, i) => (
                    <span key={i} className="px-2 py-0.5 rounded bg-slate-100 text-xs text-slate-600">
                      {"{" + v + "}"}
                    </span>
                  ))}
                </div>
              </div>
              <div className="text-sm text-slate-400">
                v{template.version || 1}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
  
  return (
    <ProfessionalLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin/super")}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Communication Center</h1>
              <p className="text-slate-500">Supervision des communications (Twilio & Resend)</p>
            </div>
          </div>
        </div>
        
        {/* Stats */}
        {renderStats()}
        
        {/* Provider Status */}
        {renderProviderStatus()}
        
        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mb-6 w-fit">
          <button
            onClick={() => setActiveTab("logs")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "logs" ? "bg-white text-primary shadow-sm" : "text-slate-600 hover:text-slate-800"
            }`}
          >
            <MessageSquare className="w-4 h-4 inline mr-2" />
            Historique
          </button>
          <button
            onClick={() => setActiveTab("templates")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "templates" ? "bg-white text-primary shadow-sm" : "text-slate-600 hover:text-slate-800"
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Templates
          </button>
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {activeTab === "logs" && renderLogs()}
            {activeTab === "templates" && renderTemplates()}
          </>
        )}
      </div>
    </ProfessionalLayout>
  );
}
